import React from "react";

type ButtonSize = "small" | "medium" | "large";
type ButtonVariant = "primary" | "secondary" | "plain" | "neutral"; // primary = black, secondary = purple, plain = text-only, neutral = grey

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  size?: ButtonSize;
  variant?: ButtonVariant;
  loading?: boolean;
  rounded?: boolean;
  children: React.ReactNode;
}

const sizeToClasses: Record<ButtonSize, string> = {
  small: "h-[40px] px-4 text-sm",
  medium: "h-[48px] px-5 text-base",
  large: "h-[54px] px-6 text-base",
};

const iconSizeToClasses: Record<ButtonSize, string> = {
  small: "h-[32px] w-[32px] p-2",
  medium: "h-[40px] w-[40px] p-2",
  large: "h-[48px] w-[48px] p-2",
};

const variantToClasses: Record<ButtonVariant, string> = {
  primary: "bg-[#200044] hover:bg-purple-700 text-white",
  secondary: "bg-purple-600 hover:bg-purple-700 text-white",
  plain: "bg-transparent text-[#200044] hover:underline hover:text-purple-700",
  neutral: "bg-gray-100 hover:bg-gray-200 text-[#16181D]",
};

export default function Button({
  size = "medium",
  variant = "primary",
  loading = false,
  rounded = false,
  children,
  className = "",
  disabled,
  ...props
}: ButtonProps) {
  // Check if children contain Arabic text
  const hasArabicText =
    typeof children === "string" && /[\u0600-\u06FF]/.test(children);
  const base =
    "tracking-tight font-semibold transition-colors cursor-pointer disabled:cursor-not-allowed";
  const widthClass =
    variant === "plain"
      ? "w-auto"
      : variant === "neutral" && rounded
        ? "w-auto"
        : "w-full";
  const shapeClass = rounded
    ? "rounded-full"
    : variant === "plain"
      ? ""
      : "rounded-full";
  const sizeClass =
    variant === "neutral" && rounded
      ? iconSizeToClasses[size]
      : sizeToClasses[size];

  const isDisabled = disabled || loading;

  // Override colors when loading or disabled - force grey styling
  const getVariantClasses = () => {
    if (loading || disabled) {
      return "bg-[#F4F6F9] text-[#7E82AD] hover:bg-[#F4F6F9]";
    }
    return variantToClasses[variant];
  };

  return (
    <button
      className={[
        base,
        widthClass,
        shapeClass,
        getVariantClasses(),
        ...(variant === "plain" ? [] : [sizeClass]),
        hasArabicText ? "arabic-text" : "english-text",
        className,
      ].join(" ")}
      disabled={isDisabled}
      {...props}
    >
      {loading ? (
        <div className="flex items-center justify-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#7E82AD] border-t-transparent"></div>
          <span>{children}</span>
        </div>
      ) : (
        children
      )}
    </button>
  );
}
